import Keycloak from "keycloak-js";
import { clearAuthSession } from "./authSession";
import { redirectToAppsPortalIfEnabled } from "./appsPortal";

const url = process.env.REACT_APP_KEYCLOAK_URL;
const realm = process.env.REACT_APP_KEYCLOAK_REALM;
const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID;

/** Indica si hay valores de entorno suficientes para hablar con Keycloak. */
export const isKeycloakConfigured = () => Boolean(url && realm && clientId);

/** Instancia única del cliente Keycloak. */
export const keycloak = new Keycloak({ url, realm, clientId });

/**
 * Salida por sesión SSO terminada: limpia la sesión propia de la app (el JWT
 * emitido por el backend no expira solo porque la sesión de Keycloak haya
 * terminado) y saca al usuario. Con el portal centralizado activo la salida es
 * hacia el portal; si no, se conserva la redirección interna a login.
 */
export const endLocalSession = () => {
  clearAuthSession();

  if (redirectToAppsPortalIfEnabled()) {
    return;
  }

  window.location.href = "/login";
};

// Margen de vigencia pedido al renovar: fuerza la renovación (y con ella la
// verificación contra Keycloak) cuando al token le queda menos de este tiempo.
const MIN_TOKEN_VALIDITY_SECONDS = 60;

// Periodo de la revalidación de respaldo. El iframe de sesión de keycloak-js
// sondea cada 5 s, pero se auto-deshabilita cuando el navegador bloquea
// cookies de terceros (Safari, Firefox estricto, incógnito): ahí este ciclo es
// lo único que detecta un cierre de sesión hecho en un sitio hermano.
const SESSION_WATCH_INTERVAL_MS = 60 * 1000;

// Evita ráfagas de revalidaciones cuando varios disparadores coinciden
// (p. ej. `focus` y `visibilitychange` al volver a la pestaña).
const SESSION_WATCH_THROTTLE_MS = 10 * 1000;

// keycloak-js dispara este callback cada vez que descarta el token: cuando el
// iframe de sesión detecta un cambio (Single Logout desde otra app del realm)
// y cuando el refresh token deja de ser válido porque la sesión SSO terminó.
keycloak.onAuthLogout = () => {
  endLocalSession();
};

// Sin esto el access_token de Keycloak (minutos de vida) queda vencido en
// memoria: se pierde la renovación y, con ella, la única comprobación de
// sesión que NO depende de cookies de terceros.
keycloak.onTokenExpired = () => {
  ensureSsoSessionAlive();
};

/**
 * Revalida la sesión SSO contra Keycloak.
 *
 * `updateToken` chequea el iframe de sesión cuando está habilitado y pide un
 * token nuevo con el refresh token. Si la sesión SSO ya fue cerrada (en este o
 * en otro sitio del realm), Keycloak responde 400 al refresh y keycloak-js
 * descarta el token, lo que dispara `onAuthLogout` y cierra también la sesión
 * local. Los errores de red no cierran sesión: solo se registran.
 *
 * @param {boolean} force `true` fuerza la ida al servidor aunque el token siga
 *   vigente. Es lo que hace fiable la detección cuando el navegador bloquea
 *   cookies de terceros y el iframe de sesión quedó deshabilitado.
 * @returns {Promise<boolean>} `true` si la sesión sigue viva.
 */
export const ensureSsoSessionAlive = async (force = false) => {
  if (!isKeycloakConfigured() || !keycloak.authenticated) {
    return false;
  }

  try {
    await keycloak.updateToken(force ? -1 : MIN_TOKEN_VALIDITY_SECONDS);
    return true;
  } catch (error) {
    console.warn("No fue posible revalidar la sesión institucional:", error);
    return Boolean(keycloak.authenticated);
  }
};

let watchStarted = false;
let lastCheckAt = 0;

/**
 * Arranca la vigilancia de la sesión SSO: revalida periódicamente y cada vez
 * que el usuario vuelve a la pestaña (al despertar el equipo o al cambiar de
 * aplicación, que es justo cuando pudo haberse cerrado sesión en otro sitio).
 * Solo corre con la pestaña visible, para no gastar peticiones en segundo
 * plano. Idempotente.
 */
export const startSsoSessionWatch = () => {
  if (watchStarted || !isKeycloakConfigured()) {
    return;
  }
  watchStarted = true;

  const revalidate = () => {
    const now = Date.now();

    if (now - lastCheckAt < SESSION_WATCH_THROTTLE_MS) {
      return;
    }
    lastCheckAt = now;

    ensureSsoSessionAlive(true);
  };

  const revalidateIfVisible = () => {
    if (document.visibilityState === "visible") {
      revalidate();
    }
  };

  document.addEventListener("visibilitychange", revalidateIfVisible);
  window.addEventListener("focus", revalidate);
  window.setInterval(revalidateIfVisible, SESSION_WATCH_INTERVAL_MS);

  // Propagación inmediata entre pestañas de esta misma app: `storage` solo se
  // dispara en las OTRAS pestañas, así que si una limpió la sesión (logout,
  // 401 o cierre SSO detectado allí), esta sale en el acto sin esperar su
  // propio ciclo de revisión.
  window.addEventListener("storage", (event) => {
    if (event.key === "token" && event.newValue === null) {
      endLocalSession();
    }
  });
};

// keycloak-js solo permite llamar a `keycloak.init()` una vez por instancia
// (una segunda llamada lanza "A 'Keycloak' instance can only be initialized
// once"). Cacheamos la promesa para que cualquier código que invoque
// initializeKeycloakSso() más de una vez en el mismo ciclo de vida de la
// página reciba el mismo resultado en vez de romper el arranque.
let initPromise = null;

/**
 * Inicializa Keycloak con chequeo silencioso (check-sso): si el usuario ya
 * tiene sesión institucional activa (p. ej. abierta desde otro portal del
 * mismo realm), se autentica sin mostrar ninguna pantalla. Idempotente.
 */
export const initializeKeycloakSso = async () => {
  if (!isKeycloakConfigured()) {
    console.warn(
      "Keycloak SSO no está configurado (faltan REACT_APP_KEYCLOAK_URL/REALM/CLIENT_ID)."
    );
    return false;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = keycloak
    .init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      pkceMethod: "S256",
      checkLoginIframe: true,
    })
    .catch((error) => {
      console.error("Error inicializando Keycloak SSO:", error);
      // Permite reintentar en una llamada posterior en vez de quedar
      // atascado con una promesa fallida cacheada para siempre.
      initPromise = null;
      return false;
    });

  return initPromise;
};
